/**
 * 入庫 · 收貨對數**單行**（`IntakeVerifyForm` 用；`ui-spec` 頁 6）
 *
 * 一行 = 一件貨：顯示申報箱數 → 入實收／損壞 → 有差異就出「原因」格 →
 * 有損壞就出「上載箱相」（`rules §七 B4`：損壞一定要有相）。
 * ⚠️ 呢個檔唔識 API：所有嘢由 `IntakeVerifyForm` 傳入（保持單純、易測）。
 */
import { useTranslation } from 'react-i18next';
import FormTextField from '@coeldery/form-text-field';
import { btnPrimary, hint as hintStyle } from '@coeldery/ui-kit';
import type { IntakeLine } from '../lib/api';

export interface VerifyRowState {
  receivedCartons: string;
  damagedCartons: string;
  note: string;
  evidenceIds: string[];
}

export interface IntakeVerifyRowProps {
  line: IntakeLine;
  row: VerifyRowState;
  uploading: boolean;
  onChange: (patch: Partial<VerifyRowState>) => void;
  onPickPhoto: (file: File) => void;
}

export default function IntakeVerifyRow({
  line,
  row,
  uploading,
  onChange,
  onPickPhoto,
}: IntakeVerifyRowProps) {
  const { t } = useTranslation();
  const received = Number(row.receivedCartons);
  const dirty = Number.isFinite(received) && received !== line.declaredCartons;
  const damaged = Number(row.damagedCartons) > 0;

  return (
    <div
      style={{
        border: '1px solid var(--color-divider)',
        borderRadius: '12px',
        padding: '12px',
        marginBottom: '10px',
      }}
    >
      <strong>{line.itemName ?? '—'}</strong>
      <span style={hintStyle}> {line.barcode ?? ''}</span>
      <p style={hintStyle}>
        {t('intake.declaredCartons')}：{line.declaredCartons}
      </p>

      <div style={{ display: 'grid', gap: '10px' }}>
        <FormTextField
          id={`f-rcv-${line.id}`}
          labelKey="intake.receivedCartons"
          type="number"
          value={row.receivedCartons}
          onChange={(v) => onChange({ receivedCartons: v ?? '' })}
        />
        <FormTextField
          id={`f-dmg-${line.id}`}
          labelKey="intake.damagedCartons"
          type="number"
          value={row.damagedCartons}
          onChange={(v) => onChange({ damagedCartons: v ?? '' })}
        />
        {dirty && (
          <FormTextField
            id={`f-note-${line.id}`}
            labelKey="intake.diffReason"
            value={row.note}
            onChange={(v) => onChange({ note: v ?? '' })}
          />
        )}
        {damaged && (
          <div>
            <label style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center' }}>
              {uploading ? t('intake.uploading') : t('intake.uploadDamage')}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) onPickPhoto(file);
                }}
              />
            </label>
            <p style={hintStyle}>
              {t('intake.damagePhoto')}：{row.evidenceIds.length}
              {row.evidenceIds.length > 0 ? ` · ${t('intake.uploaded')}` : ''}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
